import type {Experiment, Pointer} from '@wix/document-services-types'
import type {ViewerAPI} from '@wix/viewer-manager-interface'
import {deepClone} from '@wix/wix-immutable-proxy'
import _ from 'lodash'
import type {ViewerApiCallsManager} from './debug/viewerInterfaceCallsTrace'
import {removeOutOfScopeData} from './scopesUtils'
import type {ViewerManagerAdapterConfig, ViewerManagerDAL} from './types'
import {getGetterForInnerPath, getGetterForTypeFn} from './typeToGetter'
import {getSetterForInnerPath, getSetterForTypeFn} from './typeToSetter'

const wrapViewerInterfaceWithLogger = (viewer: ViewerAPI, viewerApiTrace: ViewerApiCallsManager) => {
    _.forEach(viewer, (methods, namespace) => {
        _.forEach(methods, (method, methodName) => {
            if (_.isFunction(method)) {
                const originalFunction = method as Function
                viewer[namespace][methodName] = (...args: any[]) => {
                    const res = originalFunction(...args)
                    if (viewerApiTrace.shouldLogTrace()) {
                        viewerApiTrace.logTrace({namespace, methodName, args, res})
                    }
                    return res
                }
            }
        })
    })
}

const createDAL = (
    viewer: ViewerAPI,
    viewerApiTrace: ViewerApiCallsManager,
    experimentInstance: Experiment,
    config?: ViewerManagerAdapterConfig
): ViewerManagerDAL => {
    if (config?.isDebugMode) {
        wrapViewerInterfaceWithLogger(viewer, viewerApiTrace)
    }

    const typeToSetter = getSetterForTypeFn(viewer)
    const typeToSetterForInnerItem = getSetterForInnerPath(viewer)
    const typeToGetter = getGetterForTypeFn(viewer, experimentInstance)
    const typeToGetterForInnerItem = getGetterForInnerPath(viewer)

    const get = (pointer: Pointer, enableScopes?: boolean) => {
        if (!_.isString(pointer.id) || !_.isString(pointer.type)) {
            return
        }
        if (pointer.innerPath?.length) {
            const innerItemGetter = typeToGetterForInnerItem(pointer.type)
            //no throw here, because we generically handle get from innerPath if there is no specific implementation for it
            if (innerItemGetter) {
                return innerItemGetter(pointer.id, pointer.innerPath)
            }
        }
        const getter = typeToGetter(pointer.type)
        if (!getter) {
            throw new Error(`No getter registered for type ${pointer.type}`)
        }
        let value = getter(pointer.id, pointer.type, experimentInstance)

        if (enableScopes) {
            value = removeOutOfScopeData(pointer, value)
        }

        return pointer.innerPath ? _.get(value, pointer.innerPath) : value
    }

    const set = (pointer: Pointer, value: any) => {
        if (pointer.innerPath?.length) {
            const innerItemSetter = typeToSetterForInnerItem(pointer.type)
            if (!innerItemSetter) {
                throw new Error(`No inner-setter registered for type ${pointer.type}`)
            }
            innerItemSetter(pointer.id, pointer.innerPath as string[], deepClone(value))
            return
        }

        const setter = typeToSetter(pointer.type)
        if (!setter) {
            throw new Error(`No setter registered for type ${pointer.type}`)
        }
        setter(pointer.id, deepClone(value), pointer.type, experimentInstance)
    }

    const isExist = (pointer: Pointer) => !!get(pointer)

    const remove = (pointer: Pointer) => set(pointer, undefined)

    return {
        get,
        set,
        isExist,
        remove
    }
}

export {createDAL}
